import { Languages } from "@app/types/multilingual"

/** CLDR plural categories (`Intl.PluralRules`' `select()` return values). */
export type PluralCategory = "zero" | "one" | "two" | "few" | "many" | "other";

/** A count-dependent phrase: one variant per grammatical plural form a
 * language distinguishes. `other` is the universal fallback every locale
 * has; the rest are only filled in for locales whose CLDR rules use them.
 * Use `{count}` inside a variant as the interpolation point for the number. */
export type PluralTranslation = Partial<Record<PluralCategory, string>> & { other: string };

interface translation {
    CallDriver: string,
    OrderDetails: string,
    EstPickup: string,
    EstDelivery: string,
    TrackYourOrder: string,
    CheckTheStatus: string,
    TypeTrackNumber: string,
    TypeNoSpace: string,
    Track: string,
    orderNotFound: string,
    orderExpired: string,
    Block: string,
    Floor: string,
    Room: string,
    StopsLeftToDeliver: PluralTranslation,
    Delivering: string,
    Delivered: string,
    PickingUpTheOrder: string,
    Pickup: string,
    Dropoff: string,
    ViewProof: string,
    ProofOfPickup: string,
    ProofOfDelivery: string,
    Customer: string,
    Driver: string,
    Service: string,
    Vehicle: string,
    CouldntLoadCustomer: string,
    CouldntLoadDriver: string,
    CouldntLoadService: string,
    CouldntLoadVehicle: string,
    CouldntLoadOrder: string,
    CouldntLoadStop: string,
    CouldntLoadEventHistory: string,
    NoDriverAssigned: string,
    NoVehicleAssigned: string,
    Events: string,
    NoEventsYet: string,
    EventsWillShowUpHere: string,
    WeCouldntFindThisOrder: string,
    DoubleCheckTheLink: string,
    Details: string,
    AddressDetails: string,
    CustomerDescription: string,
    Proof: string,
    NoProofYet: string,
    ProofWillAppearHere: string,
    LastEvent: string,
    Note: string,
    GoBack: string,
    Close: string,
    CompanyLogo: string,
    ProofPreview: string,
    Signature: string,
    TrackPage: string,
    GoogleMapsApiKeyMissing: string,
    Call: string,
    ViewCustomFields: string,
    ViewEvents: string,
    ViewStopDetails: string,
    Today: string,
}

export const tStore : {
    [key in  Languages]: translation;
} = {
    en: {
        CallDriver: 'Call Driver',
        OrderDetails: 'Order Details',
        EstPickup: 'Est. Pickup',
        EstDelivery: 'Est. Delivery',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} Stop Left to Deliver",
            other: "{count} Stops Left to Deliver",
        },
        Delivering: "Delivering",
        Delivered: "Delivered",
        PickingUpTheOrder: "Picking up the order",
        Pickup: "Pickup",
        Dropoff: "Dropoff",
        ViewProof: "View Proof",
        ProofOfPickup: "Proof of Pickup",
        ProofOfDelivery: "Proof of Delivery",
        Customer: "Customer",
        Driver: "Driver",
        Service: "Service",
        Vehicle: "Vehicle",
        CouldntLoadCustomer: "Couldn't load this customer",
        CouldntLoadDriver: "Couldn't load this driver",
        CouldntLoadService: "Couldn't load this service",
        CouldntLoadVehicle: "Couldn't load this vehicle",
        CouldntLoadOrder: "Couldn't load this order",
        CouldntLoadStop: "Couldn't load this stop",
        CouldntLoadEventHistory: "Couldn't load event history",
        NoDriverAssigned: "No driver assigned",
        NoVehicleAssigned: "No vehicle assigned",
        Events: "Events",
        NoEventsYet: "No events yet",
        EventsWillShowUpHere: "Updates about this order will show up here.",
        WeCouldntFindThisOrder: "We couldn't find this order",
        DoubleCheckTheLink: "Double-check the link and try again.",
        Details: "Details",
        AddressDetails: "Address details",
        CustomerDescription: "Description",
        Proof: "Proof",
        NoProofYet: "No proof yet",
        ProofWillAppearHere: "Proof of pickup/delivery will appear here once available.",
        LastEvent: "Last event",
        Note: "Note",
        GoBack: "Go back",
        Close: "Close",
        CompanyLogo: "Company logo",
        ProofPreview: "Proof preview",
        Signature: "Signature",
        TrackPage: "Track Page",
        GoogleMapsApiKeyMissing: "Google Maps needs an API key. This order isn't configured with one, so the map can't load.",
        Call: "Call",
        ViewCustomFields: "View custom fields",
        ViewEvents: "View events",
        ViewStopDetails: "View details for this stop",
        Today: "Today"
    },
    fa: {
        CallDriver: 'تماس با راننده',
        OrderDetails: 'جزییات سفارش',
        EstPickup: 'زمان تقریبی تحویل گرفتن',
        EstDelivery: 'زمان تقریبی تحویل دادن',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "بلوک",
        Floor: "طبقه",
        Room: "واحد",
        StopsLeftToDeliver: {
            one: "{count} توقف باقی‌مانده برای تحویل",
            other: "{count} توقف باقی‌مانده برای تحویل",
        },
        Delivering: "در حال تحویل",
        Delivered: "تحویل داده شد",
        PickingUpTheOrder: "در حال دریافت سفارش",
        Pickup: "تحویل گرفتن",
        Dropoff: "تحویل دادن",
        ViewProof: "مشاهده مدرک",
        ProofOfPickup: "مدرک تحویل گرفتن",
        ProofOfDelivery: "مدرک تحویل دادن",
        Customer: "مشتری",
        Driver: "راننده",
        Service: "سرویس",
        Vehicle: "خودرو",
        CouldntLoadCustomer: "امکان بارگذاری اطلاعات مشتری وجود ندارد",
        CouldntLoadDriver: "امکان بارگذاری اطلاعات راننده وجود ندارد",
        CouldntLoadService: "امکان بارگذاری اطلاعات سرویس وجود ندارد",
        CouldntLoadVehicle: "امکان بارگذاری اطلاعات خودرو وجود ندارد",
        CouldntLoadOrder: "امکان بارگذاری این سفارش وجود ندارد",
        CouldntLoadStop: "امکان بارگذاری این توقف وجود ندارد",
        CouldntLoadEventHistory: "امکان بارگذاری تاریخچه رویدادها وجود ندارد",
        NoDriverAssigned: "راننده‌ای تخصیص داده نشده است",
        NoVehicleAssigned: "خودرویی تخصیص داده نشده است",
        Events: "رویدادها",
        NoEventsYet: "هنوز رویدادی ثبت نشده است",
        EventsWillShowUpHere: "به‌روزرسانی‌های این سفارش اینجا نمایش داده می‌شود.",
        WeCouldntFindThisOrder: "این سفارش پیدا نشد",
        DoubleCheckTheLink: "لینک را بررسی کنید و دوباره تلاش کنید.",
        Details: "جزئیات",
        AddressDetails: "جزئیات آدرس",
        CustomerDescription: "توضیحات",
        Proof: "مدرک",
        NoProofYet: "هنوز مدرکی ثبت نشده است",
        ProofWillAppearHere: "مدرک تحویل گرفتن/تحویل دادن پس از ثبت اینجا نمایش داده می‌شود.",
        LastEvent: "آخرین رویداد",
        Note: "یادداشت",
        GoBack: "بازگشت",
        Close: "بستن",
        CompanyLogo: "لوگوی شرکت",
        ProofPreview: "پیش‌نمایش مدرک",
        Signature: "امضا",
        TrackPage: "صفحه پیگیری",
        GoogleMapsApiKeyMissing: "نقشه گوگل به کلید API نیاز دارد. برای این سفارش کلیدی تنظیم نشده، بنابراین نقشه بارگذاری نمی‌شود.",
        Call: "تماس",
        ViewCustomFields: "مشاهده فیلدهای سفارشی",
        ViewEvents: "مشاهده رویدادها",
        ViewStopDetails: "مشاهده جزئیات این توقف",
        Today: "امروز"
    },
    de: {
        CallDriver: 'Fahrer anrufen',
        OrderDetails: 'Bestelldetails',
        EstPickup: 'Voraussichtliche Abholung',
        EstDelivery: 'Voraussichtliche Zustellung',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Gebäude",
        Floor: "Etage",
        Room: "Zimmer",
        StopsLeftToDeliver: {
            one: "{count} verbleibender Stopp",
            other: "{count} verbleibende Stopps",
        },
        Delivering: "Wird geliefert",
        Delivered: "Zugestellt",
        PickingUpTheOrder: "Bestellung wird abgeholt",
        Pickup: "Abholung",
        Dropoff: "Zustellung",
        ViewProof: "Nachweis ansehen",
        ProofOfPickup: "Abholnachweis",
        ProofOfDelivery: "Zustellnachweis",
        Customer: "Kunde",
        Driver: "Fahrer",
        Service: "Dienstleistung",
        Vehicle: "Fahrzeug",
        CouldntLoadCustomer: "Kunde konnte nicht geladen werden",
        CouldntLoadDriver: "Fahrer konnte nicht geladen werden",
        CouldntLoadService: "Dienstleistung konnte nicht geladen werden",
        CouldntLoadVehicle: "Fahrzeug konnte nicht geladen werden",
        CouldntLoadOrder: "Bestellung konnte nicht geladen werden",
        CouldntLoadStop: "Stopp konnte nicht geladen werden",
        CouldntLoadEventHistory: "Verlauf konnte nicht geladen werden",
        NoDriverAssigned: "Kein Fahrer zugewiesen",
        NoVehicleAssigned: "Kein Fahrzeug zugewiesen",
        Events: "Ereignisse",
        NoEventsYet: "Noch keine Ereignisse",
        EventsWillShowUpHere: "Aktualisierungen zu dieser Bestellung werden hier angezeigt.",
        WeCouldntFindThisOrder: "Diese Bestellung wurde nicht gefunden",
        DoubleCheckTheLink: "Überprüfen Sie den Link und versuchen Sie es erneut.",
        Details: "Details",
        AddressDetails: "Adressdetails",
        CustomerDescription: "Beschreibung",
        Proof: "Nachweis",
        NoProofYet: "Noch kein Nachweis",
        ProofWillAppearHere: "Der Nachweis für Abholung/Zustellung wird hier angezeigt, sobald verfügbar.",
        LastEvent: "Letztes Ereignis",
        Note: "Notiz",
        GoBack: "Zurück",
        Close: "Schließen",
        CompanyLogo: "Firmenlogo",
        ProofPreview: "Nachweis-Vorschau",
        Signature: "Unterschrift",
        TrackPage: "Sendungsverfolgung",
        GoogleMapsApiKeyMissing: "Google Maps benötigt einen API-Schlüssel. Für diese Bestellung ist keiner konfiguriert, daher kann die Karte nicht geladen werden.",
        Call: "Anrufen",
        ViewCustomFields: "Zusatzfelder anzeigen",
        ViewEvents: "Ereignisse anzeigen",
        ViewStopDetails: "Details zu diesem Stopp anzeigen",
        Today: "Heute"
    },
    ckb: {
        CallDriver: 'پەیوەندی بە شۆفێرەوە بکە',
        OrderDetails: 'وردەکارییەکانی داواکاری',
        EstPickup: 'کاتی خەمڵێنراوی وەرگرتن',
        EstDelivery: 'کاتی خەمڵێنراوی گەیاندن',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} وەستانی ماوە بۆ گەیاندن",
            other: "{count} وەستانی ماوە بۆ گەیاندن",
        },
        Delivering: "گەیاندن",
        Delivered: "گەیاندراوە",
        PickingUpTheOrder: "وەرگرتنی داواکاری",
        Pickup: "وەرگرتن",
        Dropoff: "گەیاندن",
        ViewProof: "بینینی بەڵگە",
        ProofOfPickup: "بەڵگەی وەرگرتن",
        ProofOfDelivery: "بەڵگەی گەیاندن",
        Customer: "کڕیار",
        Driver: "شۆفێر",
        Service: "خزمەتگوزاری",
        Vehicle: "ئۆتۆمبێل",
        CouldntLoadCustomer: "نەتوانرا زانیاری کڕیار بار بکرێت",
        CouldntLoadDriver: "نەتوانرا زانیاری شۆفێر بار بکرێت",
        CouldntLoadService: "نەتوانرا زانیاری خزمەتگوزاری بار بکرێت",
        CouldntLoadVehicle: "نەتوانرا زانیاری ئۆتۆمبێل بار بکرێت",
        CouldntLoadOrder: "نەتوانرا ئەم داواکارییە بار بکرێت",
        CouldntLoadStop: "نەتوانرا ئەم وەستانە بار بکرێت",
        CouldntLoadEventHistory: "نەتوانرا مێژووی ڕووداوەکان بار بکرێت",
        NoDriverAssigned: "هیچ شۆفێرێک دیاری نەکراوە",
        NoVehicleAssigned: "هیچ ئۆتۆمبێلێک دیاری نەکراوە",
        Events: "ڕووداوەکان",
        NoEventsYet: "هێشتا هیچ ڕووداوێک نییە",
        EventsWillShowUpHere: "نوێکردنەوەکانی ئەم داواکارییە لێرە دەردەکەون.",
        WeCouldntFindThisOrder: "ئەم داواکارییە نەدۆزرایەوە",
        DoubleCheckTheLink: "بەستەرەکە بپشکنە و دووبارە هەوڵ بدەوە.",
        Details: "وردەکاری",
        AddressDetails: "وردەکاری ناونیشان",
        CustomerDescription: "وەسف",
        Proof: "بەڵگە",
        NoProofYet: "هێشتا هیچ بەڵگەیەک نییە",
        ProofWillAppearHere: "بەڵگەی وەرگرتن/گەیاندن لێرە دەردەکەوێت کاتێک بەردەست بێت.",
        LastEvent: "دوایین ڕووداو",
        Note: "تێبینی",
        GoBack: "گەڕانەوە",
        Close: "داخستن",
        CompanyLogo: "لۆگۆی کۆمپانیا",
        ProofPreview: "پێشبینینی بەڵگە",
        Signature: "واژۆ",
        TrackPage: "پەڕەی شوێنکەوتن",
        GoogleMapsApiKeyMissing: "گووگڵ ماپس پێویستی بە کلیلی API هەیە. ئەم داواکارییە کلیلێکی بۆ دانەنراوە، بۆیە نەخشەکە بار نابێت.",
        Call: "پەیوەندی",
        ViewCustomFields: "بینینی خانە تایبەتەکان",
        ViewEvents: "بینینی ڕووداوەکان",
        ViewStopDetails: "بینینی وردەکاری ئەم وەستانە",
        Today: "ئەمڕۆ"
    },
    fr: {
        CallDriver: 'Contacter le coursier',
        OrderDetails: 'Détails de la commande',
        EstPickup: 'Récupération estimée',
        EstDelivery: 'Livraison estimée',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Bloquer",
        Floor: "Etage",
        Room: "Salle",
        StopsLeftToDeliver: {
            one: "{count} arrêt restant à livrer",
            other: "{count} arrêts restants à livrer",
        },
        Delivering: "Livraison en cours",
        Delivered: "Livré",
        PickingUpTheOrder: "Récupération de la commande",
        Pickup: "Récupération",
        Dropoff: "Livraison",
        ViewProof: "Voir la preuve",
        ProofOfPickup: "Preuve de récupération",
        ProofOfDelivery: "Preuve de livraison",
        Customer: "Client",
        Driver: "Coursier",
        Service: "Service",
        Vehicle: "Véhicule",
        CouldntLoadCustomer: "Impossible de charger ce client",
        CouldntLoadDriver: "Impossible de charger ce coursier",
        CouldntLoadService: "Impossible de charger ce service",
        CouldntLoadVehicle: "Impossible de charger ce véhicule",
        CouldntLoadOrder: "Impossible de charger cette commande",
        CouldntLoadStop: "Impossible de charger cet arrêt",
        CouldntLoadEventHistory: "Impossible de charger l'historique des événements",
        NoDriverAssigned: "Aucun coursier assigné",
        NoVehicleAssigned: "Aucun véhicule assigné",
        Events: "Événements",
        NoEventsYet: "Aucun événement pour l'instant",
        EventsWillShowUpHere: "Les mises à jour de cette commande s'afficheront ici.",
        WeCouldntFindThisOrder: "Nous n'avons pas trouvé cette commande",
        DoubleCheckTheLink: "Vérifiez le lien et réessayez.",
        Details: "Détails",
        AddressDetails: "Détails de l'adresse",
        CustomerDescription: "Description",
        Proof: "Preuve",
        NoProofYet: "Aucune preuve pour l'instant",
        ProofWillAppearHere: "La preuve de récupération/livraison s'affichera ici dès qu'elle sera disponible.",
        LastEvent: "Dernier événement",
        Note: "Note",
        GoBack: "Retour",
        Close: "Fermer",
        CompanyLogo: "Logo de l'entreprise",
        ProofPreview: "Aperçu de la preuve",
        Signature: "Signature",
        TrackPage: "Page de suivi",
        GoogleMapsApiKeyMissing: "Google Maps nécessite une clé API. Cette commande n'en a pas configuré, la carte ne peut donc pas se charger.",
        Call: "Appeler",
        ViewCustomFields: "Voir les champs personnalisés",
        ViewEvents: "Voir les événements",
        ViewStopDetails: "Voir les détails de cet arrêt",
        Today: "Aujourd'hui"
    },
    ar: {
        CallDriver: 'اتصل بالسائق',
        OrderDetails: 'تفاصيل الطلب',
        EstPickup: 'الاستلام المتوقع',
        EstDelivery: 'التسليم المتوقع',
        TrackYourOrder: "تتبع طلبك",
        CheckTheStatus: "تحقق من حالة وموقع طلباتك",
        TypeTrackNumber: "اكتب رقم التتبع هنا",
        TypeNoSpace: "اكتبه بدون مسافات بين الأحرف",
        Track: "تتبع",
        orderNotFound: "الطلب غير موجود",
        orderExpired: "رقم التتبع منتهي الصلاحية",
        Block: "المبنى",
        Floor: "الطابق",
        Room: "الغرفة",
        StopsLeftToDeliver: {
            zero: "{count} محطات متبقية للتسليم",
            one: "{count} محطة متبقية للتسليم",
            two: "{count} محطتان متبقيتان للتسليم",
            few: "{count} محطات متبقية للتسليم",
            many: "{count} محطة متبقية للتسليم",
            other: "{count} محطة متبقية للتسليم",
        },
        Delivering: "جارٍ التسليم",
        Delivered: "تم التسليم",
        PickingUpTheOrder: "جارٍ استلام الطلب",
        Pickup: "الاستلام",
        Dropoff: "التسليم",
        ViewProof: "عرض الإثبات",
        ProofOfPickup: "إثبات الاستلام",
        ProofOfDelivery: "إثبات التسليم",
        Customer: "العميل",
        Driver: "السائق",
        Service: "الخدمة",
        Vehicle: "المركبة",
        CouldntLoadCustomer: "تعذر تحميل بيانات العميل",
        CouldntLoadDriver: "تعذر تحميل بيانات السائق",
        CouldntLoadService: "تعذر تحميل بيانات الخدمة",
        CouldntLoadVehicle: "تعذر تحميل بيانات المركبة",
        CouldntLoadOrder: "تعذر تحميل هذا الطلب",
        CouldntLoadStop: "تعذر تحميل هذه المحطة",
        CouldntLoadEventHistory: "تعذر تحميل سجل الأحداث",
        NoDriverAssigned: "لم يتم تعيين سائق",
        NoVehicleAssigned: "لم يتم تعيين مركبة",
        Events: "الأحداث",
        NoEventsYet: "لا توجد أحداث بعد",
        EventsWillShowUpHere: "ستظهر هنا التحديثات المتعلقة بهذا الطلب.",
        WeCouldntFindThisOrder: "لم نتمكن من العثور على هذا الطلب",
        DoubleCheckTheLink: "تحقق من الرابط وحاول مرة أخرى.",
        Details: "التفاصيل",
        AddressDetails: "تفاصيل العنوان",
        CustomerDescription: "الوصف",
        Proof: "الإثبات",
        NoProofYet: "لا يوجد إثبات بعد",
        ProofWillAppearHere: "سيظهر إثبات الاستلام/التسليم هنا عند توفره.",
        LastEvent: "آخر حدث",
        Note: "ملاحظة",
        GoBack: "رجوع",
        Close: "إغلاق",
        CompanyLogo: "شعار الشركة",
        ProofPreview: "معاينة الإثبات",
        Signature: "التوقيع",
        TrackPage: "صفحة التتبع",
        GoogleMapsApiKeyMissing: "تحتاج خرائط جوجل إلى مفتاح API. لم يتم تكوين مفتاح لهذا الطلب، لذا لا يمكن تحميل الخريطة.",
        Call: "اتصال",
        ViewCustomFields: "عرض الحقول المخصصة",
        ViewEvents: "عرض الأحداث",
        ViewStopDetails: "عرض تفاصيل هذه المحطة",
        Today: "اليوم"
    },
    zh: {
        CallDriver: '呼叫司机',
        OrderDetails: '订单详细信息',
        EstPickup: '预计取件时间',
        EstDelivery: '预计送达时间',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            other: "{count} 个配送点待送达",
        },
        Delivering: "配送中",
        Delivered: "已送达",
        PickingUpTheOrder: "正在取件",
        Pickup: "取件",
        Dropoff: "送件",
        ViewProof: "查看凭证",
        ProofOfPickup: "取件凭证",
        ProofOfDelivery: "送达凭证",
        Customer: "客户",
        Driver: "司机",
        Service: "服务",
        Vehicle: "车辆",
        CouldntLoadCustomer: "无法加载客户信息",
        CouldntLoadDriver: "无法加载司机信息",
        CouldntLoadService: "无法加载服务信息",
        CouldntLoadVehicle: "无法加载车辆信息",
        CouldntLoadOrder: "无法加载此订单",
        CouldntLoadStop: "无法加载此站点",
        CouldntLoadEventHistory: "无法加载事件历史记录",
        NoDriverAssigned: "尚未分配司机",
        NoVehicleAssigned: "尚未分配车辆",
        Events: "事件",
        NoEventsYet: "暂无事件",
        EventsWillShowUpHere: "此订单的最新动态将显示在这里。",
        WeCouldntFindThisOrder: "未找到此订单",
        DoubleCheckTheLink: "请检查链接后重试。",
        Details: "详情",
        AddressDetails: "地址详情",
        CustomerDescription: "描述",
        Proof: "凭证",
        NoProofYet: "暂无凭证",
        ProofWillAppearHere: "取件/送达凭证将在可用后显示在这里。",
        LastEvent: "最新事件",
        Note: "备注",
        GoBack: "返回",
        Close: "关闭",
        CompanyLogo: "公司标志",
        ProofPreview: "凭证预览",
        Signature: "签名",
        TrackPage: "追踪页面",
        GoogleMapsApiKeyMissing: "Google 地图需要 API 密钥。此订单未配置密钥，因此无法加载地图。",
        Call: "呼叫",
        ViewCustomFields: "查看自定义字段",
        ViewEvents: "查看事件",
        ViewStopDetails: "查看此站点详情",
        Today: "今天"
    },
    'zh-Hant': {
        CallDriver: '呼叫司機',
        OrderDetails: '訂單詳細資訊',
        EstPickup: '預計取件時間',
        EstDelivery: '預計送達時間',
        TrackYourOrder: "追蹤您的訂單",
        CheckTheStatus: "查看訂單的狀態與位置",
        TypeTrackNumber: "請在此輸入追蹤號碼",
        TypeNoSpace: "輸入時字元之間不要有空格",
        Track: "追蹤",
        orderNotFound: "此訂單不存在",
        orderExpired: "追蹤號碼已過期",
        Block: "棟別",
        Floor: "樓層",
        Room: "房號",
        StopsLeftToDeliver: {
            other: "{count} 個配送點待送達",
        },
        Delivering: "配送中",
        Delivered: "已送達",
        PickingUpTheOrder: "正在取件",
        Pickup: "取件",
        Dropoff: "送件",
        ViewProof: "查看憑證",
        ProofOfPickup: "取件憑證",
        ProofOfDelivery: "送達憑證",
        Customer: "客戶",
        Driver: "司機",
        Service: "服務",
        Vehicle: "車輛",
        CouldntLoadCustomer: "無法載入客戶資訊",
        CouldntLoadDriver: "無法載入司機資訊",
        CouldntLoadService: "無法載入服務資訊",
        CouldntLoadVehicle: "無法載入車輛資訊",
        CouldntLoadOrder: "無法載入此訂單",
        CouldntLoadStop: "無法載入此站點",
        CouldntLoadEventHistory: "無法載入事件記錄",
        NoDriverAssigned: "尚未指派司機",
        NoVehicleAssigned: "尚未指派車輛",
        Events: "事件",
        NoEventsYet: "尚無事件",
        EventsWillShowUpHere: "此訂單的最新動態將顯示於此。",
        WeCouldntFindThisOrder: "找不到此訂單",
        DoubleCheckTheLink: "請確認連結是否正確後再試一次。",
        Details: "詳情",
        AddressDetails: "地址詳情",
        CustomerDescription: "描述",
        Proof: "憑證",
        NoProofYet: "尚無憑證",
        ProofWillAppearHere: "取件／送達憑證備妥後將顯示於此。",
        LastEvent: "最新事件",
        Note: "備註",
        GoBack: "返回",
        Close: "關閉",
        CompanyLogo: "公司標誌",
        ProofPreview: "憑證預覽",
        Signature: "簽名",
        TrackPage: "追蹤頁面",
        GoogleMapsApiKeyMissing: "Google 地圖需要 API 金鑰。此訂單尚未設定金鑰，因此無法載入地圖。",
        Call: "撥打",
        ViewCustomFields: "查看自訂欄位",
        ViewEvents: "查看事件",
        ViewStopDetails: "查看此站點詳情",
        Today: "今天"
    },
    ro: {
        CallDriver: 'Sună curierul',
        OrderDetails: 'Comanda Detalii',
        EstPickup: 'Ridicare estimată',
        EstDelivery: 'Livrare estimată',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} oprire rămasă de livrat",
            few: "{count} opriri rămase de livrat",
            other: "{count} de opriri rămase de livrat",
        },
        Delivering: "Se livrează",
        Delivered: "Livrat",
        PickingUpTheOrder: "Se ridică comanda",
        Pickup: "Ridicare",
        Dropoff: "Livrare",
        ViewProof: "Vezi dovada",
        ProofOfPickup: "Dovadă de ridicare",
        ProofOfDelivery: "Dovadă de livrare",
        Customer: "Client",
        Driver: "Curier",
        Service: "Serviciu",
        Vehicle: "Vehicul",
        CouldntLoadCustomer: "Nu s-au putut încărca datele clientului",
        CouldntLoadDriver: "Nu s-au putut încărca datele curierului",
        CouldntLoadService: "Nu s-au putut încărca datele serviciului",
        CouldntLoadVehicle: "Nu s-au putut încărca datele vehiculului",
        CouldntLoadOrder: "Comanda nu a putut fi încărcată",
        CouldntLoadStop: "Oprirea nu a putut fi încărcată",
        CouldntLoadEventHistory: "Istoricul evenimentelor nu a putut fi încărcat",
        NoDriverAssigned: "Niciun curier alocat",
        NoVehicleAssigned: "Niciun vehicul alocat",
        Events: "Evenimente",
        NoEventsYet: "Niciun eveniment încă",
        EventsWillShowUpHere: "Actualizările pentru această comandă vor apărea aici.",
        WeCouldntFindThisOrder: "Nu am găsit această comandă",
        DoubleCheckTheLink: "Verifică linkul și încearcă din nou.",
        Details: "Detalii",
        AddressDetails: "Detalii adresă",
        CustomerDescription: "Descriere",
        Proof: "Dovadă",
        NoProofYet: "Nicio dovadă încă",
        ProofWillAppearHere: "Dovada de ridicare/livrare va apărea aici imediat ce este disponibilă.",
        LastEvent: "Ultimul eveniment",
        Note: "Notă",
        GoBack: "Înapoi",
        Close: "Închide",
        CompanyLogo: "Sigla companiei",
        ProofPreview: "Previzualizare dovadă",
        Signature: "Semnătură",
        TrackPage: "Pagină de urmărire",
        GoogleMapsApiKeyMissing: "Google Maps necesită o cheie API. Această comandă nu are una configurată, așa că harta nu se poate încărca.",
        Call: "Sună",
        ViewCustomFields: "Vezi câmpurile personalizate",
        ViewEvents: "Vezi evenimentele",
        ViewStopDetails: "Vezi detaliile acestei opriri",
        Today: "Astăzi"
    },
    nl: {
        CallDriver: 'Bel koerier',
        OrderDetails: 'Bestel Details',
        EstPickup: 'Geschatte ophaaltijd',
        EstDelivery: 'Geschatte bezorgtijd',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} resterende stop om te bezorgen",
            other: "{count} resterende stops om te bezorgen",
        },
        Delivering: "Wordt bezorgd",
        Delivered: "Bezorgd",
        PickingUpTheOrder: "Bestelling wordt opgehaald",
        Pickup: "Ophalen",
        Dropoff: "Bezorgen",
        ViewProof: "Bewijs bekijken",
        ProofOfPickup: "Bewijs van ophalen",
        ProofOfDelivery: "Bewijs van bezorgen",
        Customer: "Klant",
        Driver: "Koerier",
        Service: "Dienst",
        Vehicle: "Voertuig",
        CouldntLoadCustomer: "Kan klantgegevens niet laden",
        CouldntLoadDriver: "Kan koeriersgegevens niet laden",
        CouldntLoadService: "Kan dienstgegevens niet laden",
        CouldntLoadVehicle: "Kan voertuiggegevens niet laden",
        CouldntLoadOrder: "Kan deze bestelling niet laden",
        CouldntLoadStop: "Kan dit stoppunt niet laden",
        CouldntLoadEventHistory: "Kan de gebeurtenissengeschiedenis niet laden",
        NoDriverAssigned: "Geen koerier toegewezen",
        NoVehicleAssigned: "Geen voertuig toegewezen",
        Events: "Gebeurtenissen",
        NoEventsYet: "Nog geen gebeurtenissen",
        EventsWillShowUpHere: "Updates over deze bestelling verschijnen hier.",
        WeCouldntFindThisOrder: "We konden deze bestelling niet vinden",
        DoubleCheckTheLink: "Controleer de link en probeer het opnieuw.",
        Details: "Details",
        AddressDetails: "Adresgegevens",
        CustomerDescription: "Beschrijving",
        Proof: "Bewijs",
        NoProofYet: "Nog geen bewijs",
        ProofWillAppearHere: "Bewijs van ophalen/bezorgen verschijnt hier zodra het beschikbaar is.",
        LastEvent: "Laatste gebeurtenis",
        Note: "Notitie",
        GoBack: "Terug",
        Close: "Sluiten",
        CompanyLogo: "Bedrijfslogo",
        ProofPreview: "Voorbeeld van bewijs",
        Signature: "Handtekening",
        TrackPage: "Trackpagina",
        GoogleMapsApiKeyMissing: "Google Maps heeft een API-sleutel nodig. Deze bestelling heeft er geen ingesteld, dus de kaart kan niet worden geladen.",
        Call: "Bel",
        ViewCustomFields: "Aangepaste velden bekijken",
        ViewEvents: "Gebeurtenissen bekijken",
        ViewStopDetails: "Details van dit stoppunt bekijken",
        Today: "Vandaag"
    },
    fi: {
        CallDriver: 'Soita Kuljettajalle',
        OrderDetails: 'Tilauksen tiedot',
        EstPickup: 'Arvioitu noutoaika',
        EstDelivery: 'Arvioitu toimitusaika',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} pysähdys jäljellä toimitukseen",
            other: "{count} pysähdystä jäljellä toimitukseen",
        },
        Delivering: "Toimitetaan",
        Delivered: "Toimitettu",
        PickingUpTheOrder: "Noudetaan tilausta",
        Pickup: "Nouto",
        Dropoff: "Toimitus",
        ViewProof: "Näytä todiste",
        ProofOfPickup: "Noudon todiste",
        ProofOfDelivery: "Toimituksen todiste",
        Customer: "Asiakas",
        Driver: "Kuljettaja",
        Service: "Palvelu",
        Vehicle: "Ajoneuvo",
        CouldntLoadCustomer: "Asiakastietoja ei voitu ladata",
        CouldntLoadDriver: "Kuljettajan tietoja ei voitu ladata",
        CouldntLoadService: "Palvelun tietoja ei voitu ladata",
        CouldntLoadVehicle: "Ajoneuvon tietoja ei voitu ladata",
        CouldntLoadOrder: "Tilausta ei voitu ladata",
        CouldntLoadStop: "Pysähdystä ei voitu ladata",
        CouldntLoadEventHistory: "Tapahtumahistoriaa ei voitu ladata",
        NoDriverAssigned: "Kuljettajaa ei ole vielä määritetty",
        NoVehicleAssigned: "Ajoneuvoa ei ole vielä määritetty",
        Events: "Tapahtumat",
        NoEventsYet: "Ei vielä tapahtumia",
        EventsWillShowUpHere: "Tämän tilauksen päivitykset näkyvät täällä.",
        WeCouldntFindThisOrder: "Tilausta ei löytynyt",
        DoubleCheckTheLink: "Tarkista linkki ja yritä uudelleen.",
        Details: "Tiedot",
        AddressDetails: "Osoitteen tiedot",
        CustomerDescription: "Kuvaus",
        Proof: "Todiste",
        NoProofYet: "Ei vielä todistetta",
        ProofWillAppearHere: "Nouto- tai toimitustodiste näkyy täällä, kun se on saatavilla.",
        LastEvent: "Viimeisin tapahtuma",
        Note: "Muistiinpano",
        GoBack: "Takaisin",
        Close: "Sulje",
        CompanyLogo: "Yrityksen logo",
        ProofPreview: "Todisteen esikatselu",
        Signature: "Allekirjoitus",
        TrackPage: "Seurantasivu",
        GoogleMapsApiKeyMissing: "Google Maps tarvitsee API-avaimen. Tälle tilaukselle ei ole määritetty avainta, joten karttaa ei voida ladata.",
        Call: "Soita",
        ViewCustomFields: "Näytä mukautetut kentät",
        ViewEvents: "Näytä tapahtumat",
        ViewStopDetails: "Näytä tämän pysähdyksen tiedot",
        Today: "Tänään"
    },
    es: {
        CallDriver: 'Llamar al conductor',
        OrderDetails: 'Detalles del pedido',
        EstPickup: 'Recogida estimada',
        EstDelivery: 'Entrega estimada',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Bloque/Edificio",
        Floor: "Piso",
        Room: "Cuarto",
        StopsLeftToDeliver: {
            one: "{count} parada restante por entregar",
            other: "{count} paradas restantes por entregar",
        },
        Delivering: "Entregando",
        Delivered: "Entregado",
        PickingUpTheOrder: "Recogiendo el pedido",
        Pickup: "Recogida",
        Dropoff: "Entrega",
        ViewProof: "Ver comprobante",
        ProofOfPickup: "Comprobante de recogida",
        ProofOfDelivery: "Comprobante de entrega",
        Customer: "Cliente",
        Driver: "Conductor",
        Service: "Servicio",
        Vehicle: "Vehículo",
        CouldntLoadCustomer: "No se pudo cargar la información del cliente",
        CouldntLoadDriver: "No se pudo cargar la información del conductor",
        CouldntLoadService: "No se pudo cargar la información del servicio",
        CouldntLoadVehicle: "No se pudo cargar la información del vehículo",
        CouldntLoadOrder: "No se pudo cargar este pedido",
        CouldntLoadStop: "No se pudo cargar esta parada",
        CouldntLoadEventHistory: "No se pudo cargar el historial de eventos",
        NoDriverAssigned: "Ningún conductor asignado",
        NoVehicleAssigned: "Ningún vehículo asignado",
        Events: "Eventos",
        NoEventsYet: "Aún no hay eventos",
        EventsWillShowUpHere: "Las actualizaciones de este pedido aparecerán aquí.",
        WeCouldntFindThisOrder: "No pudimos encontrar este pedido",
        DoubleCheckTheLink: "Verifica el enlace e inténtalo de nuevo.",
        Details: "Detalles",
        AddressDetails: "Detalles de la dirección",
        CustomerDescription: "Descripción",
        Proof: "Comprobante",
        NoProofYet: "Aún no hay comprobante",
        ProofWillAppearHere: "El comprobante de recogida/entrega aparecerá aquí en cuanto esté disponible.",
        LastEvent: "Último evento",
        Note: "Nota",
        GoBack: "Volver",
        Close: "Cerrar",
        CompanyLogo: "Logotipo de la empresa",
        ProofPreview: "Vista previa del comprobante",
        Signature: "Firma",
        TrackPage: "Página de seguimiento",
        GoogleMapsApiKeyMissing: "Google Maps necesita una clave de API. Este pedido no tiene una configurada, por lo que el mapa no se puede cargar.",
        Call: "Llamar",
        ViewCustomFields: "Ver campos personalizados",
        ViewEvents: "Ver eventos",
        ViewStopDetails: "Ver detalles de esta parada",
        Today: "Hoy"
    },
    it: {
        CallDriver: `Chiama l'autista`,
        OrderDetails: `Dettagli dell'ordine`,
        EstPickup: 'Ritiro stimato',
        EstDelivery: 'Consegna stimata',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} fermata rimasta da consegnare",
            other: "{count} fermate rimaste da consegnare",
        },
        Delivering: "In consegna",
        Delivered: "Consegnato",
        PickingUpTheOrder: "Ritiro dell'ordine in corso",
        Pickup: "Ritiro",
        Dropoff: "Consegna",
        ViewProof: "Vedi la prova",
        ProofOfPickup: "Prova di ritiro",
        ProofOfDelivery: "Prova di consegna",
        Customer: "Cliente",
        Driver: "Autista",
        Service: "Servizio",
        Vehicle: "Veicolo",
        CouldntLoadCustomer: "Impossibile caricare il cliente",
        CouldntLoadDriver: "Impossibile caricare l'autista",
        CouldntLoadService: "Impossibile caricare il servizio",
        CouldntLoadVehicle: "Impossibile caricare il veicolo",
        CouldntLoadOrder: "Impossibile caricare questo ordine",
        CouldntLoadStop: "Impossibile caricare questa fermata",
        CouldntLoadEventHistory: "Impossibile caricare la cronologia degli eventi",
        NoDriverAssigned: "Nessun autista assegnato",
        NoVehicleAssigned: "Nessun veicolo assegnato",
        Events: "Eventi",
        NoEventsYet: "Ancora nessun evento",
        EventsWillShowUpHere: "Gli aggiornamenti relativi a questo ordine appariranno qui.",
        WeCouldntFindThisOrder: "Non siamo riusciti a trovare questo ordine",
        DoubleCheckTheLink: "Controlla il link e riprova.",
        Details: "Dettagli",
        AddressDetails: "Dettagli dell'indirizzo",
        CustomerDescription: "Descrizione",
        Proof: "Prova",
        NoProofYet: "Ancora nessuna prova",
        ProofWillAppearHere: "La prova di ritiro/consegna apparirà qui non appena disponibile.",
        LastEvent: "Ultimo evento",
        Note: "Nota",
        GoBack: "Indietro",
        Close: "Chiudi",
        CompanyLogo: "Logo dell'azienda",
        ProofPreview: "Anteprima della prova",
        Signature: "Firma",
        TrackPage: "Pagina di tracciamento",
        GoogleMapsApiKeyMissing: "Google Maps richiede una chiave API. Questo ordine non ne ha una configurata, quindi la mappa non può essere caricata.",
        Call: "Chiama",
        ViewCustomFields: "Visualizza campi personalizzati",
        ViewEvents: "Visualizza eventi",
        ViewStopDetails: "Visualizza dettagli di questa fermata",
        Today: "Oggi"
    },
    az: {
        CallDriver: `Sürücüyə zəng et`,
        OrderDetails: `Sifariş detalları`,
        EstPickup: 'Təxmini götürmə vaxtı',
        EstDelivery: 'Təxmini çatdırma vaxtı',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Blok",
        Floor: "Mərtəbə",
        Room: "Mənzil",
        StopsLeftToDeliver: {
            one: "{count} dayanacaq çatdırılmaq üçün qalıb",
            other: "{count} dayanacaq çatdırılmaq üçün qalıb",
        },
        Delivering: "Çatdırılır",
        Delivered: "Çatdırıldı",
        PickingUpTheOrder: "Sifariş götürülür",
        Pickup: "Götürmə",
        Dropoff: "Çatdırma",
        ViewProof: "Sübutu göstər",
        ProofOfPickup: "Götürmə sübutu",
        ProofOfDelivery: "Çatdırma sübutu",
        Customer: "Müştəri",
        Driver: "Sürücü",
        Service: "Xidmət",
        Vehicle: "Nəqliyyat vasitəsi",
        CouldntLoadCustomer: "Müştəri məlumatları yüklənə bilmədi",
        CouldntLoadDriver: "Sürücü məlumatları yüklənə bilmədi",
        CouldntLoadService: "Xidmət məlumatları yüklənə bilmədi",
        CouldntLoadVehicle: "Nəqliyyat vasitəsi məlumatları yüklənə bilmədi",
        CouldntLoadOrder: "Bu sifariş yüklənə bilmədi",
        CouldntLoadStop: "Bu dayanacaq yüklənə bilmədi",
        CouldntLoadEventHistory: "Hadisə tarixçəsi yüklənə bilmədi",
        NoDriverAssigned: "Sürücü təyin edilməyib",
        NoVehicleAssigned: "Nəqliyyat vasitəsi təyin edilməyib",
        Events: "Hadisələr",
        NoEventsYet: "Hələ heç bir hadisə yoxdur",
        EventsWillShowUpHere: "Bu sifarişlə bağlı yeniliklər burada görünəcək.",
        WeCouldntFindThisOrder: "Bu sifarişi tapa bilmədik",
        DoubleCheckTheLink: "Linki yoxlayın və yenidən cəhd edin.",
        Details: "Təfərrüatlar",
        AddressDetails: "Ünvan təfərrüatları",
        CustomerDescription: "Təsvir",
        Proof: "Sübut",
        NoProofYet: "Hələ heç bir sübut yoxdur",
        ProofWillAppearHere: "Götürmə/çatdırılma sübutu mövcud olan kimi burada görünəcək.",
        LastEvent: "Son hadisə",
        Note: "Qeyd",
        GoBack: "Geri",
        Close: "Bağla",
        CompanyLogo: "Şirkət loqosu",
        ProofPreview: "Sübutun önbaxışı",
        Signature: "İmza",
        TrackPage: "İzləmə səhifəsi",
        GoogleMapsApiKeyMissing: "Google Maps API açarı tələb edir. Bu sifariş üçün açar tənzimlənməyib, ona görə xəritə yüklənə bilmir.",
        Call: "Zəng et",
        ViewCustomFields: "Xüsusi sahələrə bax",
        ViewEvents: "Hadisələrə bax",
        ViewStopDetails: "Bu dayanacağın təfərrüatlarına bax",
        Today: "Bu gün"
    },
    ru: {
        CallDriver: 'Позвонить водителю',
        OrderDetails: 'Детали заказа',
        EstPickup: 'Ориентировочное время забора',
        EstDelivery: 'Ориентировочное время доставки',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} остановка осталась до доставки",
            few: "{count} остановки осталось до доставки",
            many: "{count} остановок осталось до доставки",
            other: "{count} остановки осталось до доставки",
        },
        Delivering: "Доставляется",
        Delivered: "Доставлено",
        PickingUpTheOrder: "Забор заказа",
        Pickup: "Забор",
        Dropoff: "Доставка",
        ViewProof: "Посмотреть подтверждение",
        ProofOfPickup: "Подтверждение получения",
        ProofOfDelivery: "Подтверждение доставки",
        Customer: "Клиент",
        Driver: "Водитель",
        Service: "Услуга",
        Vehicle: "Автомобиль",
        CouldntLoadCustomer: "Не удалось загрузить данные клиента",
        CouldntLoadDriver: "Не удалось загрузить данные водителя",
        CouldntLoadService: "Не удалось загрузить данные услуги",
        CouldntLoadVehicle: "Не удалось загрузить данные автомобиля",
        CouldntLoadOrder: "Не удалось загрузить этот заказ",
        CouldntLoadStop: "Не удалось загрузить эту остановку",
        CouldntLoadEventHistory: "Не удалось загрузить историю событий",
        NoDriverAssigned: "Водитель не назначен",
        NoVehicleAssigned: "Автомобиль не назначен",
        Events: "События",
        NoEventsYet: "Событий пока нет",
        EventsWillShowUpHere: "Обновления по этому заказу будут отображаться здесь.",
        WeCouldntFindThisOrder: "Не удалось найти этот заказ",
        DoubleCheckTheLink: "Проверьте ссылку и попробуйте снова.",
        Details: "Детали",
        AddressDetails: "Детали адреса",
        CustomerDescription: "Описание",
        Proof: "Подтверждение",
        NoProofYet: "Подтверждения пока нет",
        ProofWillAppearHere: "Подтверждение получения/доставки появится здесь, как только будет доступно.",
        LastEvent: "Последнее событие",
        Note: "Заметка",
        GoBack: "Назад",
        Close: "Закрыть",
        CompanyLogo: "Логотип компании",
        ProofPreview: "Предпросмотр подтверждения",
        Signature: "Подпись",
        TrackPage: "Страница отслеживания",
        GoogleMapsApiKeyMissing: "Для Google Maps требуется API-ключ. Для этого заказа он не настроен, поэтому карта не может загрузиться.",
        Call: "Позвонить",
        ViewCustomFields: "Показать дополнительные поля",
        ViewEvents: "Показать события",
        ViewStopDetails: "Показать детали этой остановки",
        Today: "Сегодня"
    },
    tr: {
        CallDriver: `Sürücüyü ara`,
        OrderDetails: `Sipariş Detayları`,
        EstPickup: 'Tahmini alım saati',
        EstDelivery: 'Tahmini teslimat saati',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} teslim edilecek durak kaldı",
            other: "{count} teslim edilecek durak kaldı",
        },
        Delivering: "Teslim ediliyor",
        Delivered: "Teslim edildi",
        PickingUpTheOrder: "Sipariş alınıyor",
        Pickup: "Alım",
        Dropoff: "Teslimat",
        ViewProof: "Kanıtı görüntüle",
        ProofOfPickup: "Alım kanıtı",
        ProofOfDelivery: "Teslimat kanıtı",
        Customer: "Müşteri",
        Driver: "Sürücü",
        Service: "Hizmet",
        Vehicle: "Araç",
        CouldntLoadCustomer: "Müşteri bilgileri yüklenemedi",
        CouldntLoadDriver: "Sürücü bilgileri yüklenemedi",
        CouldntLoadService: "Hizmet bilgileri yüklenemedi",
        CouldntLoadVehicle: "Araç bilgileri yüklenemedi",
        CouldntLoadOrder: "Bu sipariş yüklenemedi",
        CouldntLoadStop: "Bu durak yüklenemedi",
        CouldntLoadEventHistory: "Etkinlik geçmişi yüklenemedi",
        NoDriverAssigned: "Sürücü atanmadı",
        NoVehicleAssigned: "Araç atanmadı",
        Events: "Etkinlikler",
        NoEventsYet: "Henüz etkinlik yok",
        EventsWillShowUpHere: "Bu siparişle ilgili güncellemeler burada görünecek.",
        WeCouldntFindThisOrder: "Bu siparişi bulamadık",
        DoubleCheckTheLink: "Bağlantıyı kontrol edip tekrar deneyin.",
        Details: "Detaylar",
        AddressDetails: "Adres detayları",
        CustomerDescription: "Açıklama",
        Proof: "Kanıt",
        NoProofYet: "Henüz kanıt yok",
        ProofWillAppearHere: "Alım/teslimat kanıtı hazır olduğunda burada görünecek.",
        LastEvent: "Son etkinlik",
        Note: "Not",
        GoBack: "Geri",
        Close: "Kapat",
        CompanyLogo: "Şirket logosu",
        ProofPreview: "Kanıt önizlemesi",
        Signature: "İmza",
        TrackPage: "Takip sayfası",
        GoogleMapsApiKeyMissing: "Google Haritalar bir API anahtarı gerektiriyor. Bu sipariş için bir anahtar yapılandırılmamış, bu yüzden harita yüklenemiyor.",
        Call: "Ara",
        ViewCustomFields: "Özel alanları görüntüle",
        ViewEvents: "Etkinlikleri görüntüle",
        ViewStopDetails: "Bu durağın detaylarını görüntüle",
        Today: "Bugün"
    },
    pt: {
        CallDriver: 'Ligar para entregador',
        OrderDetails: 'Detalhes da Encomenda',
        EstPickup: 'Recolha prevista',
        EstDelivery: 'Entrega prevista',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} paragem restante para entregar",
            other: "{count} paragens restantes para entregar",
        },
        Delivering: "A entregar",
        Delivered: "Entregue",
        PickingUpTheOrder: "A recolher a encomenda",
        Pickup: "Recolha",
        Dropoff: "Entrega",
        ViewProof: "Ver comprovativo",
        ProofOfPickup: "Comprovativo de recolha",
        ProofOfDelivery: "Comprovativo de entrega",
        Customer: "Cliente",
        Driver: "Entregador",
        Service: "Serviço",
        Vehicle: "Veículo",
        CouldntLoadCustomer: "Não foi possível carregar o cliente",
        CouldntLoadDriver: "Não foi possível carregar o entregador",
        CouldntLoadService: "Não foi possível carregar o serviço",
        CouldntLoadVehicle: "Não foi possível carregar o veículo",
        CouldntLoadOrder: "Não foi possível carregar esta encomenda",
        CouldntLoadStop: "Não foi possível carregar esta paragem",
        CouldntLoadEventHistory: "Não foi possível carregar o histórico de eventos",
        NoDriverAssigned: "Nenhum entregador atribuído",
        NoVehicleAssigned: "Nenhum veículo atribuído",
        Events: "Eventos",
        NoEventsYet: "Ainda sem eventos",
        EventsWillShowUpHere: "As atualizações desta encomenda vão aparecer aqui.",
        WeCouldntFindThisOrder: "Não foi possível encontrar esta encomenda",
        DoubleCheckTheLink: "Verifique o link e tente novamente.",
        Details: "Detalhes",
        AddressDetails: "Detalhes da morada",
        CustomerDescription: "Descrição",
        Proof: "Comprovativo",
        NoProofYet: "Ainda sem comprovativo",
        ProofWillAppearHere: "O comprovativo de recolha/entrega vai aparecer aqui assim que estiver disponível.",
        LastEvent: "Último evento",
        Note: "Nota",
        GoBack: "Voltar",
        Close: "Fechar",
        CompanyLogo: "Logótipo da empresa",
        ProofPreview: "Pré-visualização do comprovativo",
        Signature: "Assinatura",
        TrackPage: "Página de rastreio",
        GoogleMapsApiKeyMissing: "O Google Maps precisa de uma chave de API. Esta encomenda não tem uma configurada, por isso o mapa não pode ser carregado.",
        Call: "Ligar",
        ViewCustomFields: "Ver campos personalizados",
        ViewEvents: "Ver eventos",
        ViewStopDetails: "Ver detalhes desta paragem",
        Today: "Hoje"
    },
    el: {
        CallDriver: 'Κλήση Οδηγού',
        OrderDetails: 'Λεπτομέρειες Παραγγελίας',
        EstPickup: 'Εκτιμώμενη παραλαβή',
        EstDelivery: 'Εκτιμώμενη παράδοση',
        TrackYourOrder: "Εντοπισμός Διανομής",
        CheckTheStatus: "Ελέγξτε την κατάσταση και την τοποθεσία των παραγγελιών σας",
        TypeTrackNumber: "Πληκτρολογήστε εδώ τον αριθμό παρακολούθησης",
        TypeNoSpace: "Πληκτρολογήστε χωρίς κενό μεταξύ των χαρακτήρων",
        Track: "Εντοπισμός",
        orderNotFound: "Η παραγγελία δεν υπάρχει",
        orderExpired: "Ο κωδικός παρακολούθησης έχει λήξει ",
        Block: "Οικοδομικό τετράγωνο",
        Floor: "Όροφος",
        Room: "Δωμάτιο",
        StopsLeftToDeliver: {
            one: "{count} στάση απομένει για παράδοση",
            other: "{count} στάσεις απομένουν για παράδοση",
        },
        Delivering: "Σε παράδοση",
        Delivered: "Παραδόθηκε",
        PickingUpTheOrder: "Παραλαβή της παραγγελίας",
        Pickup: "Παραλαβή",
        Dropoff: "Παράδοση",
        ViewProof: "Προβολή απόδειξης",
        ProofOfPickup: "Απόδειξη παραλαβής",
        ProofOfDelivery: "Απόδειξη παράδοσης",
        Customer: "Πελάτης",
        Driver: "Οδηγός",
        Service: "Υπηρεσία",
        Vehicle: "Όχημα",
        CouldntLoadCustomer: "Δεν ήταν δυνατή η φόρτωση του πελάτη",
        CouldntLoadDriver: "Δεν ήταν δυνατή η φόρτωση του οδηγού",
        CouldntLoadService: "Δεν ήταν δυνατή η φόρτωση της υπηρεσίας",
        CouldntLoadVehicle: "Δεν ήταν δυνατή η φόρτωση του οχήματος",
        CouldntLoadOrder: "Δεν ήταν δυνατή η φόρτωση αυτής της παραγγελίας",
        CouldntLoadStop: "Δεν ήταν δυνατή η φόρτωση αυτής της στάσης",
        CouldntLoadEventHistory: "Δεν ήταν δυνατή η φόρτωση του ιστορικού συμβάντων",
        NoDriverAssigned: "Δεν έχει ανατεθεί οδηγός",
        NoVehicleAssigned: "Δεν έχει ανατεθεί όχημα",
        Events: "Συμβάντα",
        NoEventsYet: "Δεν υπάρχουν ακόμα συμβάντα",
        EventsWillShowUpHere: "Οι ενημερώσεις για αυτή την παραγγελία θα εμφανίζονται εδώ.",
        WeCouldntFindThisOrder: "Δεν βρήκαμε αυτή την παραγγελία",
        DoubleCheckTheLink: "Ελέγξτε τον σύνδεσμο και δοκιμάστε ξανά.",
        Details: "Λεπτομέρειες",
        AddressDetails: "Λεπτομέρειες διεύθυνσης",
        CustomerDescription: "Περιγραφή",
        Proof: "Απόδειξη",
        NoProofYet: "Δεν υπάρχει ακόμα απόδειξη",
        ProofWillAppearHere: "Η απόδειξη παραλαβής/παράδοσης θα εμφανιστεί εδώ μόλις είναι διαθέσιμη.",
        LastEvent: "Τελευταίο συμβάν",
        Note: "Σημείωση",
        GoBack: "Πίσω",
        Close: "Κλείσιμο",
        CompanyLogo: "Λογότυπο εταιρείας",
        ProofPreview: "Προεπισκόπηση απόδειξης",
        Signature: "Υπογραφή",
        TrackPage: "Σελίδα εντοπισμού",
        GoogleMapsApiKeyMissing: "Ο Χάρτης Google χρειάζεται κλειδί API. Αυτή η παραγγελία δεν έχει ρυθμισμένο κλειδί, γι' αυτό ο χάρτης δεν μπορεί να φορτωθεί.",
        Call: "Κλήση",
        ViewCustomFields: "Προβολή προσαρμοσμένων πεδίων",
        ViewEvents: "Προβολή συμβάντων",
        ViewStopDetails: "Προβολή λεπτομερειών αυτής της στάσης",
        Today: "Σήμερα"
    },
    'de-AT': {
        CallDriver: 'Fahrer anrufen',
        OrderDetails: 'Bestelldetails',
        EstPickup: 'Voraussichtliche Abholung',
        EstDelivery: 'Voraussichtliche Zustellung',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Stiege",
        Floor: "Stock",
        Room: "Türnummer",
        StopsLeftToDeliver: {
            one: "{count} verbleibender Stopp",
            other: "{count} verbleibende Stopps",
        },
        Delivering: "Wird geliefert",
        Delivered: "Zugestellt",
        PickingUpTheOrder: "Bestellung wird abgeholt",
        Pickup: "Abholung",
        Dropoff: "Zustellung",
        ViewProof: "Nachweis ansehen",
        ProofOfPickup: "Abholnachweis",
        ProofOfDelivery: "Zustellnachweis",
        Customer: "Kunde",
        Driver: "Fahrer",
        Service: "Dienstleistung",
        Vehicle: "Fahrzeug",
        CouldntLoadCustomer: "Kunde konnte nicht geladen werden",
        CouldntLoadDriver: "Fahrer konnte nicht geladen werden",
        CouldntLoadService: "Dienstleistung konnte nicht geladen werden",
        CouldntLoadVehicle: "Fahrzeug konnte nicht geladen werden",
        CouldntLoadOrder: "Bestellung konnte nicht geladen werden",
        CouldntLoadStop: "Stopp konnte nicht geladen werden",
        CouldntLoadEventHistory: "Verlauf konnte nicht geladen werden",
        NoDriverAssigned: "Kein Fahrer zugewiesen",
        NoVehicleAssigned: "Kein Fahrzeug zugewiesen",
        Events: "Ereignisse",
        NoEventsYet: "Noch keine Ereignisse",
        EventsWillShowUpHere: "Aktualisierungen zu dieser Bestellung werden hier angezeigt.",
        WeCouldntFindThisOrder: "Diese Bestellung wurde nicht gefunden",
        DoubleCheckTheLink: "Überprüfen Sie den Link und versuchen Sie es erneut.",
        Details: "Details",
        AddressDetails: "Adressdetails",
        CustomerDescription: "Beschreibung",
        Proof: "Nachweis",
        NoProofYet: "Noch kein Nachweis",
        ProofWillAppearHere: "Der Nachweis für Abholung/Zustellung wird hier angezeigt, sobald verfügbar.",
        LastEvent: "Letztes Ereignis",
        Note: "Notiz",
        GoBack: "Zurück",
        Close: "Schließen",
        CompanyLogo: "Firmenlogo",
        ProofPreview: "Nachweis-Vorschau",
        Signature: "Unterschrift",
        TrackPage: "Sendungsverfolgung",
        GoogleMapsApiKeyMissing: "Google Maps benötigt einen API-Schlüssel. Für diese Bestellung ist keiner konfiguriert, daher kann die Karte nicht geladen werden.",
        Call: "Anrufen",
        ViewCustomFields: "Zusatzfelder anzeigen",
        ViewEvents: "Ereignisse anzeigen",
        ViewStopDetails: "Details zu diesem Stopp anzeigen",
        Today: "Heute"
    },
    ku: {
        CallDriver: 'پەیوەندیکردن لەگەڵ شۆفێر',
        OrderDetails: 'وردەکارییەکانی داواکاری',
        EstPickup: 'کاتی خەمڵێنراوی وەرگرتن',
        EstDelivery: 'کاتی خەمڵێنراوی گەیاندن',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} وەستانی ماوە بۆ گەیاندن",
            other: "{count} وەستانی ماوە بۆ گەیاندن",
        },
        Delivering: "گەیاندن",
        Delivered: "گەیاندراوە",
        PickingUpTheOrder: "وەرگرتنی داواکاری",
        Pickup: "وەرگرتن",
        Dropoff: "گەیاندن",
        ViewProof: "بینینی بەڵگە",
        ProofOfPickup: "بەڵگەی وەرگرتن",
        ProofOfDelivery: "بەڵگەی گەیاندن",
        Customer: "کڕیار",
        Driver: "شۆفێر",
        Service: "خزمەتگوزاری",
        Vehicle: "ئۆتۆمبێل",
        CouldntLoadCustomer: "نەتوانرا زانیاری کڕیار بار بکرێت",
        CouldntLoadDriver: "نەتوانرا زانیاری شۆفێر بار بکرێت",
        CouldntLoadService: "نەتوانرا زانیاری خزمەتگوزاری بار بکرێت",
        CouldntLoadVehicle: "نەتوانرا زانیاری ئۆتۆمبێل بار بکرێت",
        CouldntLoadOrder: "نەتوانرا ئەم داواکارییە بار بکرێت",
        CouldntLoadStop: "نەتوانرا ئەم وەستانە بار بکرێت",
        CouldntLoadEventHistory: "نەتوانرا مێژووی ڕووداوەکان بار بکرێت",
        NoDriverAssigned: "هیچ شۆفێرێک دیاری نەکراوە",
        NoVehicleAssigned: "هیچ ئۆتۆمبێلێک دیاری نەکراوە",
        Events: "ڕووداوەکان",
        NoEventsYet: "هێشتا هیچ ڕووداوێک نییە",
        EventsWillShowUpHere: "نوێکردنەوەکانی ئەم داواکارییە لێرە دەردەکەون.",
        WeCouldntFindThisOrder: "ئەم داواکارییە نەدۆزرایەوە",
        DoubleCheckTheLink: "بەستەرەکە بپشکنە و دووبارە هەوڵ بدەوە.",
        Details: "وردەکاری",
        AddressDetails: "وردەکاری ناونیشان",
        CustomerDescription: "وەسف",
        Proof: "بەڵگە",
        NoProofYet: "هێشتا هیچ بەڵگەیەک نییە",
        ProofWillAppearHere: "بەڵگەی وەرگرتن/گەیاندن لێرە دەردەکەوێت کاتێک بەردەست بێت.",
        LastEvent: "دوایین ڕووداو",
        Note: "تێبینی",
        GoBack: "گەڕانەوە",
        Close: "داخستن",
        CompanyLogo: "لۆگۆی کۆمپانیا",
        ProofPreview: "پێشبینینی بەڵگە",
        Signature: "واژۆ",
        TrackPage: "پەڕەی شوێنکەوتن",
        GoogleMapsApiKeyMissing: "گووگڵ ماپس پێویستی بە کلیلی API هەیە. ئەم داواکارییە کلیلێکی بۆ دانەنراوە، بۆیە نەخشەکە بار نابێت.",
        Call: "پەیوەندی",
        ViewCustomFields: "بینینی خانە تایبەتەکان",
        ViewEvents: "بینینی ڕووداوەکان",
        ViewStopDetails: "بینینی وردەکاری ئەم وەستانە",
        Today: "ئەمڕۆ"
    },
    he: {
        CallDriver: 'התקשר לנהג',
        OrderDetails: 'פרטי הזמנה',
        EstPickup: 'איסוף משוער',
        EstDelivery: 'מסירה משוערת',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "בלוק",
        Floor: "קומה",
        Room: "חדר",
        StopsLeftToDeliver: {
            one: "{count} תחנה נותרה למסירה",
            two: "{count} תחנות נותרו למסירה",
            other: "{count} תחנות נותרו למסירה",
        },
        Delivering: "במסירה",
        Delivered: "נמסר",
        PickingUpTheOrder: "אוסף את ההזמנה",
        Pickup: "איסוף",
        Dropoff: "מסירה",
        ViewProof: "צפה באישור",
        ProofOfPickup: "אישור איסוף",
        ProofOfDelivery: "אישור מסירה",
        Customer: "לקוח",
        Driver: "נהג",
        Service: "שירות",
        Vehicle: "רכב",
        CouldntLoadCustomer: "לא ניתן היה לטעון את פרטי הלקוח",
        CouldntLoadDriver: "לא ניתן היה לטעון את פרטי הנהג",
        CouldntLoadService: "לא ניתן היה לטעון את פרטי השירות",
        CouldntLoadVehicle: "לא ניתן היה לטעון את פרטי הרכב",
        CouldntLoadOrder: "לא ניתן היה לטעון את ההזמנה",
        CouldntLoadStop: "לא ניתן היה לטעון את התחנה",
        CouldntLoadEventHistory: "לא ניתן היה לטעון את היסטוריית האירועים",
        NoDriverAssigned: "לא שויך נהג",
        NoVehicleAssigned: "לא שויך רכב",
        Events: "אירועים",
        NoEventsYet: "אין עדיין אירועים",
        EventsWillShowUpHere: "עדכונים לגבי ההזמנה הזו יופיעו כאן.",
        WeCouldntFindThisOrder: "לא הצלחנו למצוא את ההזמנה הזו",
        DoubleCheckTheLink: "בדקו את הקישור ונסו שוב.",
        Details: "פרטים",
        AddressDetails: "פרטי כתובת",
        CustomerDescription: "תיאור",
        Proof: "אישור",
        NoProofYet: "אין עדיין אישור",
        ProofWillAppearHere: "אישור איסוף/מסירה יופיע כאן ברגע שיהיה זמין.",
        LastEvent: "האירוע האחרון",
        Note: "הערה",
        GoBack: "חזרה",
        Close: "סגור",
        CompanyLogo: "לוגו החברה",
        ProofPreview: "תצוגה מקדימה של האישור",
        Signature: "חתימה",
        TrackPage: "עמוד מעקב",
        GoogleMapsApiKeyMissing: "Google Maps דורש מפתח API. להזמנה זו לא הוגדר מפתח, ולכן לא ניתן לטעון את המפה.",
        Call: "התקשר",
        ViewCustomFields: "הצג שדות מותאמים אישית",
        ViewEvents: "הצג אירועים",
        ViewStopDetails: "הצג פרטים של תחנה זו",
        Today: "היום"
    },
    hi: {
        CallDriver: 'ड्राइवर को कॉल करें',
        OrderDetails: 'ऑर्डर विवरण',
        EstPickup: 'अनुमानित पिकअप समय',
        EstDelivery: 'अनुमानित डिलीवरी समय',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "ब्लॉक",
        Floor: "मंजिल",
        Room: "कमरा",
        StopsLeftToDeliver: {
            one: "{count} स्टॉप डिलीवर करने के लिए बाकी है",
            other: "{count} स्टॉप डिलीवर करने के लिए बाकी हैं",
        },
        Delivering: "डिलीवर हो रहा है",
        Delivered: "डिलीवर हो गया",
        PickingUpTheOrder: "ऑर्डर पिकअप हो रहा है",
        Pickup: "पिकअप",
        Dropoff: "ड्रॉपऑफ",
        ViewProof: "प्रमाण देखें",
        ProofOfPickup: "पिकअप का प्रमाण",
        ProofOfDelivery: "डिलीवरी का प्रमाण",
        Customer: "ग्राहक",
        Driver: "ड्राइवर",
        Service: "सेवा",
        Vehicle: "वाहन",
        CouldntLoadCustomer: "ग्राहक की जानकारी लोड नहीं हो सकी",
        CouldntLoadDriver: "ड्राइवर की जानकारी लोड नहीं हो सकी",
        CouldntLoadService: "सेवा की जानकारी लोड नहीं हो सकी",
        CouldntLoadVehicle: "वाहन की जानकारी लोड नहीं हो सकी",
        CouldntLoadOrder: "यह ऑर्डर लोड नहीं हो सका",
        CouldntLoadStop: "यह स्टॉप लोड नहीं हो सका",
        CouldntLoadEventHistory: "इवेंट इतिहास लोड नहीं हो सका",
        NoDriverAssigned: "कोई ड्राइवर नियुक्त नहीं किया गया",
        NoVehicleAssigned: "कोई वाहन नियुक्त नहीं किया गया",
        Events: "इवेंट",
        NoEventsYet: "अभी तक कोई इवेंट नहीं",
        EventsWillShowUpHere: "इस ऑर्डर से जुड़े अपडेट यहाँ दिखाई देंगे।",
        WeCouldntFindThisOrder: "हमें यह ऑर्डर नहीं मिला",
        DoubleCheckTheLink: "लिंक जाँचें और फिर से कोशिश करें।",
        Details: "विवरण",
        AddressDetails: "पते का विवरण",
        CustomerDescription: "विवरण",
        Proof: "प्रमाण",
        NoProofYet: "अभी तक कोई प्रमाण नहीं",
        ProofWillAppearHere: "पिकअप/डिलीवरी का प्रमाण उपलब्ध होते ही यहाँ दिखाई देगा।",
        LastEvent: "अंतिम इवेंट",
        Note: "नोट",
        GoBack: "वापस जाएं",
        Close: "बंद करें",
        CompanyLogo: "कंपनी का लोगो",
        ProofPreview: "प्रमाण का पूर्वावलोकन",
        Signature: "हस्ताक्षर",
        TrackPage: "ट्रैक पेज",
        GoogleMapsApiKeyMissing: "Google Maps को एक API कुंजी चाहिए। इस ऑर्डर के लिए कोई कुंजी सेट नहीं है, इसलिए मानचित्र लोड नहीं हो सकता।",
        Call: "कॉल करें",
        ViewCustomFields: "कस्टम फ़ील्ड देखें",
        ViewEvents: "इवेंट देखें",
        ViewStopDetails: "इस स्टॉप का विवरण देखें",
        Today: "आज"
    },
    ms: {
        CallDriver: 'Hubungi Pemandu',
        OrderDetails: 'Butiran Pesanan',
        EstPickup: 'Anggaran masa pengambilan',
        EstDelivery: 'Anggaran masa penghantaran',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            other: "{count} perhentian lagi untuk dihantar",
        },
        Delivering: "Sedang dihantar",
        Delivered: "Telah dihantar",
        PickingUpTheOrder: "Mengambil pesanan",
        Pickup: "Pengambilan",
        Dropoff: "Penghantaran",
        ViewProof: "Lihat bukti",
        ProofOfPickup: "Bukti pengambilan",
        ProofOfDelivery: "Bukti penghantaran",
        Customer: "Pelanggan",
        Driver: "Pemandu",
        Service: "Perkhidmatan",
        Vehicle: "Kenderaan",
        CouldntLoadCustomer: "Maklumat pelanggan tidak dapat dimuatkan",
        CouldntLoadDriver: "Maklumat pemandu tidak dapat dimuatkan",
        CouldntLoadService: "Maklumat perkhidmatan tidak dapat dimuatkan",
        CouldntLoadVehicle: "Maklumat kenderaan tidak dapat dimuatkan",
        CouldntLoadOrder: "Pesanan ini tidak dapat dimuatkan",
        CouldntLoadStop: "Perhentian ini tidak dapat dimuatkan",
        CouldntLoadEventHistory: "Sejarah acara tidak dapat dimuatkan",
        NoDriverAssigned: "Tiada pemandu ditugaskan",
        NoVehicleAssigned: "Tiada kenderaan ditugaskan",
        Events: "Acara",
        NoEventsYet: "Belum ada acara",
        EventsWillShowUpHere: "Kemas kini untuk pesanan ini akan dipaparkan di sini.",
        WeCouldntFindThisOrder: "Kami tidak dapat menemui pesanan ini",
        DoubleCheckTheLink: "Semak pautan dan cuba lagi.",
        Details: "Butiran",
        AddressDetails: "Butiran alamat",
        CustomerDescription: "Penerangan",
        Proof: "Bukti",
        NoProofYet: "Belum ada bukti",
        ProofWillAppearHere: "Bukti pengambilan/penghantaran akan dipaparkan di sini apabila tersedia.",
        LastEvent: "Acara terkini",
        Note: "Nota",
        GoBack: "Kembali",
        Close: "Tutup",
        CompanyLogo: "Logo syarikat",
        ProofPreview: "Pratonton bukti",
        Signature: "Tandatangan",
        TrackPage: "Halaman penjejakan",
        GoogleMapsApiKeyMissing: "Google Maps memerlukan kunci API. Pesanan ini tidak mempunyai kunci yang dikonfigurasikan, jadi peta tidak dapat dimuatkan.",
        Call: "Hubungi",
        ViewCustomFields: "Lihat medan tersuai",
        ViewEvents: "Lihat acara",
        ViewStopDetails: "Lihat butiran perhentian ini",
        Today: "Hari ini"
    },
    "pt-BR": {
        CallDriver: 'Chamar Motorista',
        OrderDetails: 'Detalhes do Pedido',
        EstPickup: 'Retirada estimada',
        EstDelivery: 'Entrega estimada',
        TrackYourOrder: "Rastreie seu pedido",
        CheckTheStatus: "Verifique o status e a localização dos seus pedidos",
        TypeTrackNumber: "Digite seu número de rastreamento aqui",
        TypeNoSpace: "Digite sem espaço entre os caracteres",
        Track: "Rastrear",
        orderNotFound: "O pedido não existe",
        orderExpired: "O número de rastreamento expirou",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} parada restante para entregar",
            other: "{count} paradas restantes para entregar",
        },
        Delivering: "Entregando",
        Delivered: "Entregue",
        PickingUpTheOrder: "Retirando o pedido",
        Pickup: "Retirada",
        Dropoff: "Entrega",
        ViewProof: "Ver comprovante",
        ProofOfPickup: "Comprovante de retirada",
        ProofOfDelivery: "Comprovante de entrega",
        Customer: "Cliente",
        Driver: "Motorista",
        Service: "Serviço",
        Vehicle: "Veículo",
        CouldntLoadCustomer: "Não foi possível carregar o cliente",
        CouldntLoadDriver: "Não foi possível carregar o motorista",
        CouldntLoadService: "Não foi possível carregar o serviço",
        CouldntLoadVehicle: "Não foi possível carregar o veículo",
        CouldntLoadOrder: "Não foi possível carregar este pedido",
        CouldntLoadStop: "Não foi possível carregar esta parada",
        CouldntLoadEventHistory: "Não foi possível carregar o histórico de eventos",
        NoDriverAssigned: "Nenhum motorista atribuído",
        NoVehicleAssigned: "Nenhum veículo atribuído",
        Events: "Eventos",
        NoEventsYet: "Ainda sem eventos",
        EventsWillShowUpHere: "As atualizações deste pedido vão aparecer aqui.",
        WeCouldntFindThisOrder: "Não conseguimos encontrar este pedido",
        DoubleCheckTheLink: "Verifique o link e tente novamente.",
        Details: "Detalhes",
        AddressDetails: "Detalhes do endereço",
        CustomerDescription: "Descrição",
        Proof: "Comprovante",
        NoProofYet: "Ainda sem comprovante",
        ProofWillAppearHere: "O comprovante de retirada/entrega vai aparecer aqui assim que estiver disponível.",
        LastEvent: "Último evento",
        Note: "Nota",
        GoBack: "Voltar",
        Close: "Fechar",
        CompanyLogo: "Logotipo da empresa",
        ProofPreview: "Pré-visualização do comprovante",
        Signature: "Assinatura",
        TrackPage: "Página de rastreamento",
        GoogleMapsApiKeyMissing: "O Google Maps precisa de uma chave de API. Este pedido não tem uma configurada, por isso o mapa não pode ser carregado.",
        Call: "Ligar",
        ViewCustomFields: "Ver campos personalizados",
        ViewEvents: "Ver eventos",
        ViewStopDetails: "Ver detalhes desta parada",
        Today: "Hoje"
    },
    cs: {
        CallDriver: 'Zavolat řidiči',
        OrderDetails: 'Detaily objednávky',
        EstPickup: 'Odhadované vyzvednutí',
        EstDelivery: 'Odhadované doručení',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Blok",
        Floor: "Patro",
        Room: "Pokoj",
        StopsLeftToDeliver: {
            one: "{count} zastávka zbývá doručit",
            few: "{count} zastávky zbývají doručit",
            other: "{count} zastávek zbývá doručit",
        },
        Delivering: "Doručuje se",
        Delivered: "Doručeno",
        PickingUpTheOrder: "Vyzvedává se objednávka",
        Pickup: "Vyzvednutí",
        Dropoff: "Doručení",
        ViewProof: "Zobrazit potvrzení",
        ProofOfPickup: "Potvrzení o vyzvednutí",
        ProofOfDelivery: "Potvrzení o doručení",
        Customer: "Zákazník",
        Driver: "Řidič",
        Service: "Služba",
        Vehicle: "Vozidlo",
        CouldntLoadCustomer: "Údaje o zákazníkovi se nepodařilo načíst",
        CouldntLoadDriver: "Údaje o řidiči se nepodařilo načíst",
        CouldntLoadService: "Údaje o službě se nepodařilo načíst",
        CouldntLoadVehicle: "Údaje o vozidle se nepodařilo načíst",
        CouldntLoadOrder: "Tuto objednávku se nepodařilo načíst",
        CouldntLoadStop: "Tuto zastávku se nepodařilo načíst",
        CouldntLoadEventHistory: "Historii událostí se nepodařilo načíst",
        NoDriverAssigned: "Nebyl přiřazen žádný řidič",
        NoVehicleAssigned: "Nebylo přiřazeno žádné vozidlo",
        Events: "Události",
        NoEventsYet: "Zatím žádné události",
        EventsWillShowUpHere: "Aktualizace k této objednávce se zobrazí zde.",
        WeCouldntFindThisOrder: "Tuto objednávku se nepodařilo najít",
        DoubleCheckTheLink: "Zkontrolujte odkaz a zkuste to znovu.",
        Details: "Podrobnosti",
        AddressDetails: "Podrobnosti adresy",
        CustomerDescription: "Popis",
        Proof: "Potvrzení",
        NoProofYet: "Zatím žádné potvrzení",
        ProofWillAppearHere: "Potvrzení o vyzvednutí/doručení se zde zobrazí, jakmile bude k dispozici.",
        LastEvent: "Poslední událost",
        Note: "Poznámka",
        GoBack: "Zpět",
        Close: "Zavřít",
        CompanyLogo: "Logo společnosti",
        ProofPreview: "Náhled potvrzení",
        Signature: "Podpis",
        TrackPage: "Stránka sledování",
        GoogleMapsApiKeyMissing: "Mapy Google vyžadují klíč API. Tato objednávka jej nemá nastavený, takže mapu nelze načíst.",
        Call: "Zavolat",
        ViewCustomFields: "Zobrazit vlastní pole",
        ViewEvents: "Zobrazit události",
        ViewStopDetails: "Zobrazit podrobnosti této zastávky",
        Today: "Dnes"
    },
    hu: {
        CallDriver: 'Sofőr hívása',
        OrderDetails: 'Rendelés részletei',
        EstPickup: 'Várható átvétel',
        EstDelivery: 'Várható kiszállítás',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Épülettömb",
        Floor: "Emelet",
        Room: "Szoba",
        StopsLeftToDeliver: {
            one: "{count} hátralévő megálló a kiszállításig",
            other: "{count} hátralévő megálló a kiszállításig",
        },
        Delivering: "Kiszállítás alatt",
        Delivered: "Kiszállítva",
        PickingUpTheOrder: "Rendelés átvétele folyamatban",
        Pickup: "Átvétel",
        Dropoff: "Kiszállítás",
        ViewProof: "Igazolás megtekintése",
        ProofOfPickup: "Átvételi igazolás",
        ProofOfDelivery: "Kiszállítási igazolás",
        Customer: "Ügyfél",
        Driver: "Sofőr",
        Service: "Szolgáltatás",
        Vehicle: "Jármű",
        CouldntLoadCustomer: "Az ügyfél adatai nem tölthetők be",
        CouldntLoadDriver: "A sofőr adatai nem tölthetők be",
        CouldntLoadService: "A szolgáltatás adatai nem tölthetők be",
        CouldntLoadVehicle: "A jármű adatai nem tölthetők be",
        CouldntLoadOrder: "Ez a rendelés nem tölthető be",
        CouldntLoadStop: "Ez a megálló nem tölthető be",
        CouldntLoadEventHistory: "Az események előzményei nem tölthetők be",
        NoDriverAssigned: "Nincs hozzárendelt sofőr",
        NoVehicleAssigned: "Nincs hozzárendelt jármű",
        Events: "Események",
        NoEventsYet: "Még nincsenek események",
        EventsWillShowUpHere: "A rendeléssel kapcsolatos frissítések itt fognak megjelenni.",
        WeCouldntFindThisOrder: "Nem találtuk meg ezt a rendelést",
        DoubleCheckTheLink: "Ellenőrizze a linket, és próbálja újra.",
        Details: "Részletek",
        AddressDetails: "Cím részletei",
        CustomerDescription: "Leírás",
        Proof: "Igazolás",
        NoProofYet: "Még nincs igazolás",
        ProofWillAppearHere: "Az átvételi/kézbesítési igazolás itt jelenik meg, amint elérhető.",
        LastEvent: "Legutóbbi esemény",
        Note: "Megjegyzés",
        GoBack: "Vissza",
        Close: "Bezárás",
        CompanyLogo: "Céglogó",
        ProofPreview: "Igazolás előnézete",
        Signature: "Aláírás",
        TrackPage: "Nyomkövető oldal",
        GoogleMapsApiKeyMissing: "A Google Térkép API-kulcsot igényel. Ehhez a rendeléshez nincs beállítva kulcs, ezért a térkép nem tölthető be.",
        Call: "Hívás",
        ViewCustomFields: "Egyéni mezők megtekintése",
        ViewEvents: "Események megtekintése",
        ViewStopDetails: "A megálló részleteinek megtekintése",
        Today: "Ma"
    },
    ka: {
        CallDriver: 'მძღოლთან დარეკვა',
        OrderDetails: 'შეკვეთის დეტალები',
        EstPickup: 'სავარაუდო აღების დრო',
        EstDelivery: 'სავარაუდო მიწოდების დრო',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            one: "{count} გაჩერება დარჩა მიწოდებამდე",
            other: "{count} გაჩერება დარჩა მიწოდებამდე",
        },
        Delivering: "მიწოდების პროცესშია",
        Delivered: "მიწოდებულია",
        PickingUpTheOrder: "შეკვეთის აღება მიმდინარეობს",
        Pickup: "აღება",
        Dropoff: "მიწოდება",
        ViewProof: "დადასტურების ნახვა",
        ProofOfPickup: "აღების დადასტურება",
        ProofOfDelivery: "მიწოდების დადასტურება",
        Customer: "მომხმარებელი",
        Driver: "მძღოლი",
        Service: "სერვისი",
        Vehicle: "ავტომობილი",
        CouldntLoadCustomer: "მომხმარებლის მონაცემების ჩატვირთვა ვერ მოხერხდა",
        CouldntLoadDriver: "მძღოლის მონაცემების ჩატვირთვა ვერ მოხერხდა",
        CouldntLoadService: "სერვისის მონაცემების ჩატვირთვა ვერ მოხერხდა",
        CouldntLoadVehicle: "ავტომობილის მონაცემების ჩატვირთვა ვერ მოხერხდა",
        CouldntLoadOrder: "შეკვეთის ჩატვირთვა ვერ მოხერხდა",
        CouldntLoadStop: "გაჩერების ჩატვირთვა ვერ მოხერხდა",
        CouldntLoadEventHistory: "მოვლენების ისტორიის ჩატვირთვა ვერ მოხერხდა",
        NoDriverAssigned: "მძღოლი არ არის მინიჭებული",
        NoVehicleAssigned: "ავტომობილი არ არის მინიჭებული",
        Events: "მოვლენები",
        NoEventsYet: "მოვლენები ჯერ არ არის",
        EventsWillShowUpHere: "ამ შეკვეთასთან დაკავშირებული განახლებები აქ გამოჩნდება.",
        WeCouldntFindThisOrder: "ეს შეკვეთა ვერ მოიძებნა",
        DoubleCheckTheLink: "შეამოწმეთ ბმული და სცადეთ ხელახლა.",
        Details: "დეტალები",
        AddressDetails: "მისამართის დეტალები",
        CustomerDescription: "აღწერა",
        Proof: "დადასტურება",
        NoProofYet: "დადასტურება ჯერ არ არის",
        ProofWillAppearHere: "აღების/მიწოდების დადასტურება აქ გამოჩნდება, როგორც კი ხელმისაწვდომი გახდება.",
        LastEvent: "ბოლო მოვლენა",
        Note: "შენიშვნა",
        GoBack: "უკან",
        Close: "დახურვა",
        CompanyLogo: "კომპანიის ლოგო",
        ProofPreview: "დადასტურების გადახედვა",
        Signature: "ხელმოწერა",
        TrackPage: "თვალთვალის გვერდი",
        GoogleMapsApiKeyMissing: "Google რუკებს სჭირდება API გასაღები. ამ შეკვეთისთვის გასაღები არ არის მითითებული, ამიტომ რუკა ვერ ჩაიტვირთება.",
        Call: "დარეკვა",
        ViewCustomFields: "მორგებული ველების ნახვა",
        ViewEvents: "მოვლენების ნახვა",
        ViewStopDetails: "ამ გაჩერების დეტალების ნახვა",
        Today: "დღეს"
    },
    km: {
        CallDriver: 'ហៅអ្នកបើកបរ',
        OrderDetails: 'ព័ត៌មានលម្អិតការបញ្ជាទិញ',
        EstPickup: 'ពេលវេលាប៉ាន់ស្មានទទួល',
        EstDelivery: 'ពេលវេលាប៉ាន់ស្មានប្រគល់ជូន',
        TrackYourOrder: "Track your order",
        CheckTheStatus: "Check the status and location of your orders",
        TypeTrackNumber: "Type your track number here",
        TypeNoSpace: "Type it with no space between characters",
        Track: "Track",
        orderNotFound: "The order doesn’t exist",
        orderExpired: "Tracking number is expired",
        Block: "Block",
        Floor: "Floor",
        Room: "Room",
        StopsLeftToDeliver: {
            other: "{count} ចំណុចឈប់ដែលនៅសល់ត្រូវប្រគល់ជូន",
        },
        Delivering: "កំពុងដឹកជញ្ជូន",
        Delivered: "បានប្រគល់ជូន",
        PickingUpTheOrder: "កំពុងទទួលការបញ្ជាទិញ",
        Pickup: "ការទទួល",
        Dropoff: "ការប្រគល់ជូន",
        ViewProof: "មើលភស្តុតាង",
        ProofOfPickup: "ភស្តុតាងនៃការទទួល",
        ProofOfDelivery: "ភស្តុតាងនៃការប្រគល់ជូន",
        Customer: "អតិថិជន",
        Driver: "អ្នកបើកបរ",
        Service: "សេវាកម្ម",
        Vehicle: "យានយន្ត",
        CouldntLoadCustomer: "មិនអាចផ្ទុកព័ត៌មានអតិថិជនបានទេ",
        CouldntLoadDriver: "មិនអាចផ្ទុកព័ត៌មានអ្នកបើកបរបានទេ",
        CouldntLoadService: "មិនអាចផ្ទុកព័ត៌មានសេវាកម្មបានទេ",
        CouldntLoadVehicle: "មិនអាចផ្ទុកព័ត៌មានយានយន្តបានទេ",
        CouldntLoadOrder: "មិនអាចផ្ទុកការបញ្ជាទិញនេះបានទេ",
        CouldntLoadStop: "មិនអាចផ្ទុកចំណុចឈប់នេះបានទេ",
        CouldntLoadEventHistory: "មិនអាចផ្ទុកប្រវត្តិព្រឹត្តិការណ៍បានទេ",
        NoDriverAssigned: "មិនទាន់បានចាត់តាំងអ្នកបើកបរទេ",
        NoVehicleAssigned: "មិនទាន់បានចាត់តាំងយានយន្តទេ",
        Events: "ព្រឹត្តិការណ៍",
        NoEventsYet: "មិនទាន់មានព្រឹត្តិការណ៍នៅឡើយទេ",
        EventsWillShowUpHere: "ព័ត៌មានថ្មីៗអំពីការបញ្ជាទិញនេះនឹងបង្ហាញនៅទីនេះ។",
        WeCouldntFindThisOrder: "យើងរកមិនឃើញការបញ្ជាទិញនេះទេ",
        DoubleCheckTheLink: "សូមពិនិត្យតំណរភ្ជាប់ ហើយសាកល្បងម្ដងទៀត។",
        Details: "ព័ត៌មានលម្អិត",
        AddressDetails: "ព័ត៌មានលម្អិតអំពីអាសយដ្ឋាន",
        CustomerDescription: "ការពិពណ៌នា",
        Proof: "ភស្តុតាង",
        NoProofYet: "មិនទាន់មានភស្តុតាងនៅឡើយទេ",
        ProofWillAppearHere: "ភស្តុតាងនៃការទទួល/ការចែកចាយនឹងបង្ហាញនៅទីនេះនៅពេលមាន។",
        LastEvent: "ព្រឹត្តិការណ៍ចុងក្រោយ",
        Note: "កំណត់ចំណាំ",
        GoBack: "ត្រឡប់ក្រោយ",
        Close: "បិទ",
        CompanyLogo: "ស្លាកសញ្ញាក្រុមហ៊ុន",
        ProofPreview: "មើលភស្តុតាងជាមុន",
        Signature: "ហត្ថលេខា",
        TrackPage: "ទំព័រតាមដាន",
        GoogleMapsApiKeyMissing: "Google Maps ត្រូវការសោ API។ ការបញ្ជាទិញនេះមិនទាន់បានកំណត់សោទេ ដូច្នេះផែនទីមិនអាចផ្ទុកបានទេ។",
        Call: "ហៅទូរស័ព្ទ",
        ViewCustomFields: "មើលវាលផ្ទាល់ខ្លួន",
        ViewEvents: "មើលព្រឹត្តិការណ៍",
        ViewStopDetails: "មើលព័ត៌មានលម្អិតនៃចំណុចឈប់នេះ",
        Today: "ថ្ងៃនេះ"
    },
    et: {
        CallDriver: 'Helista juhile',
        OrderDetails: 'Tellimuse üksikasjad',
        EstPickup: 'Eeldatav vastuvõtmine',
        EstDelivery: 'Eeldatav kohaletoimetamine',
        TrackYourOrder: "Jälgi oma tellimust",
        CheckTheStatus: "Kontrolli oma tellimuste staatust ja asukohta",
        TypeTrackNumber: "Sisesta jälgimisnumber siia",
        TypeNoSpace: "Sisesta ilma tühikuteta",
        Track: "Jälgi",
        orderNotFound: "Tellimust ei eksisteeri",
        orderExpired: "Jälgimisnumber on aegunud",
        Block: "Plokk",
        Floor: "Korrus",
        Room: "Tuba",
        StopsLeftToDeliver: {
            one: "{count} peatus jäänud kohaletoimetamiseks",
            other: "{count} peatust jäänud kohaletoimetamiseks",
        },
        Delivering: "Kohaletoimetamine",
        Delivered: "Kohale toimetatud",
        PickingUpTheOrder: "Tellimuse vastuvõtmine",
        Pickup: "Vastuvõtmine",
        Dropoff: "Kohaletoimetamine",
        ViewProof: "Vaata tõendit",
        ProofOfPickup: "Vastuvõtmise tõend",
        ProofOfDelivery: "Kohaletoimetamise tõend",
        Customer: "Klient",
        Driver: "Juht",
        Service: "Teenus",
        Vehicle: "Sõiduk",
        CouldntLoadCustomer: "Kliendi andmeid ei õnnestunud laadida",
        CouldntLoadDriver: "Juhi andmeid ei õnnestunud laadida",
        CouldntLoadService: "Teenuse andmeid ei õnnestunud laadida",
        CouldntLoadVehicle: "Sõiduki andmeid ei õnnestunud laadida",
        CouldntLoadOrder: "Seda tellimust ei õnnestunud laadida",
        CouldntLoadStop: "Seda peatust ei õnnestunud laadida",
        CouldntLoadEventHistory: "Sündmuste ajalugu ei õnnestunud laadida",
        NoDriverAssigned: "Juhti pole määratud",
        NoVehicleAssigned: "Sõidukit pole määratud",
        Events: "Sündmused",
        NoEventsYet: "Sündmusi pole veel",
        EventsWillShowUpHere: "Selle tellimuse värskendused kuvatakse siin.",
        WeCouldntFindThisOrder: "Me ei leidnud seda tellimust",
        DoubleCheckTheLink: "Kontrolli linki ja proovi uuesti.",
        Details: "Üksikasjad",
        AddressDetails: "Aadressi üksikasjad",
        CustomerDescription: "Kirjeldus",
        Proof: "Tõend",
        NoProofYet: "Tõendit pole veel",
        ProofWillAppearHere: "Vastuvõtmise/kohaletoimetamise tõend kuvatakse siin niipea, kui see on saadaval.",
        LastEvent: "Viimane sündmus",
        Note: "Märkus",
        GoBack: "Tagasi",
        Close: "Sulge",
        CompanyLogo: "Ettevõtte logo",
        ProofPreview: "Tõendi eelvaade",
        Signature: "Allkiri",
        TrackPage: "Jälgimisleht",
        GoogleMapsApiKeyMissing: "Google Maps vajab API-võtit. Sellel tellimusel pole võtit seadistatud, mistõttu kaarti ei saa laadida.",
        Call: "Helista",
        ViewCustomFields: "Vaata kohandatud välju",
        ViewEvents: "Vaata sündmusi",
        ViewStopDetails: "Vaata selle peatuse üksikasju",
        Today: "Täna"
    }
}

/** Keys whose value is a plain string — everything except `StopsLeftToDeliver`. */
type StringKeys = {
    [K in keyof translation]: translation[K] extends string ? K : never;
}[keyof translation];

/** Keys whose value is a `PluralTranslation` (count-dependent phrase). */
type PluralKeys = {
    [K in keyof translation]: translation[K] extends PluralTranslation ? K : never;
}[keyof translation];

export const t = (key : StringKeys , language : Languages)=>{
    if(tStore[language] && tStore[language][key]) return tStore[language][key]
    else return tStore['en'][key]
}

/** Resolves a count-dependent phrase using the target language's own CLDR
 * plural rule (via `Intl.PluralRules`) rather than always picking one form —
 * e.g. Arabic distinguishes six categories, English/German two, Chinese none. */
export const tPlural = (key : PluralKeys , count : number , language : Languages): string => {
    const entry = (tStore[language] && tStore[language][key]) ? tStore[language][key] : tStore['en'][key]
    const category = new Intl.PluralRules(language).select(count) as PluralCategory
    const template = entry[category] ?? entry.other
    return template.replace("{count}", String(count))
}
